import React from 'react';

/**
 * ServiceCard — displays a single service with icon, title, and description.
 *
 * `description` accepts either a string or an array of strings; arrays are
 * rendered as a semantic <ul> (better for screen readers and SEO than a
 * single <p> with whitespace-preserved bullet glyphs).
 *
 * Wrapped in React.memo to skip re-renders when props are unchanged.
 */
export const ServiceCard = React.memo(({ icon, title, description }) => (
    <div className="group p-6 sm:p-8 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-gold/30 hover:-translate-y-1">
        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gold/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-gold transition-colors duration-300">
            <span className="text-2xl sm:text-3xl text-gold group-hover:text-white transition-colors duration-300">
                {icon}
            </span>
        </div>
        <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3">{title}</h3>
        {Array.isArray(description) ? (
            <ul className="text-sm sm:text-base text-gray-600 leading-relaxed space-y-1.5 list-disc list-outside pl-5 marker:text-gold">
                {description.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
        ) : (
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed whitespace-pre-line">{description}</p>
        )}
    </div>
));

/**
 * WhyUsCard — displays a "why choose us" reason.
 * Wrapped in React.memo to skip re-renders when props are unchanged.
 */
export const WhyUsCard = React.memo(({ icon, title, text }) => (
    <div className="flex items-start space-x-4 p-5 sm:p-6 rounded-xl bg-white/60 backdrop-blur-sm hover:bg-white hover:shadow-md transition-all duration-300">
        <div className="flex-shrink-0 w-12 h-12 bg-gold rounded-xl flex items-center justify-center text-white text-xl">
            {icon}
        </div>
        <div>
            <h4 className="text-base sm:text-lg font-bold text-gray-800 mb-1">{title}</h4>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{text}</p>
        </div>
    </div>
));

